import { SeatType } from '@vista-digital/ocapi-types/v1';
import { Validator } from '@vista-digital/validator';
import { SeatSelectionRuleName } from '../../types/seat-selection-rule-name';
import { contiguousSeatBlockHasSingleAvailableSeat, Direction, getSeatsUntil, getSeatTwoSpacesAway, seatOneSpaceAwayIsAvailable } from '../seat-gap-utils';
import { getSeatByPosition } from '../seat-layout-utils';
import { isSeatSelected, isSeatUnavailable, isSofaSeatType } from '../seat-utils';
export const defaultSingleSeatGapFromAnAisleRuleOptions = {
    ignoreSelectedSofaSeats: true,
    allowWhenAllSeatsBetweenTheSeatGapAndAnUnavailableSeatAreSelected: true
};
export const singleSeatGapFromAnAisleRuleOptionsValidator = Validator.object({
    ignoreSelectedSofaSeats: Validator.boolean(),
    allowWhenAllSeatsBetweenTheSeatGapAndAnUnavailableSeatAreSelected: Validator.boolean()
});
const ruleName = SeatSelectionRuleName.CannotLeaveSingleSeatGapFromAisle;
/**
 * Creates a SeatSelectionRule that validates if the selected seats contain a single seat gap from an aisle.
 * The rule does not fail when the seat gap is the last remaining seat in the contiguous block.
 * The rule does not fail when the selected seat next to the gap is a Wheelchair seat or a Companion seat.
 */
export const cannotLeaveSingleSeatGapFromAisleRule = (options) => {
    const mergedOptions = Object.assign(Object.assign({}, defaultSingleSeatGapFromAnAisleRuleOptions), options);
    return {
        name: ruleName,
        validate: (data, selectedSeats) => {
            const valid = selectedSeats.every(seat => isSeatValid(data, selectedSeats, seat, mergedOptions));
            return {
                ruleName: ruleName,
                isValid: valid
            };
        }
    };
};
const isSeatValid = (data, selectedSeats, seat, options) => {
    const seatType = getSeatByPosition(data.seatLayout, seat.position).type;
    // Always make an exception for Wheelchair seats or Companion seats
    if (seatType === SeatType.Wheelchair || seatType === SeatType.Companion)
        return true;
    if (options.ignoreSelectedSofaSeats && isSofaSeatType(seatType))
        return true;
    const leftAisleValid = !hasAvailableSeatGapFromAisle(data, selectedSeats, seat.position, Direction.Left) ||
        (options.allowWhenAllSeatsBetweenTheSeatGapAndAnUnavailableSeatAreSelected &&
            allSeatsBetweenSeatAndNextUnavailableSeatAreSelected(data, selectedSeats, seat.position, Direction.Right));
    const rightAisleValid = !hasAvailableSeatGapFromAisle(data, selectedSeats, seat.position, Direction.Right) ||
        (options.allowWhenAllSeatsBetweenTheSeatGapAndAnUnavailableSeatAreSelected &&
            allSeatsBetweenSeatAndNextUnavailableSeatAreSelected(data, selectedSeats, seat.position, Direction.Left));
    return ((leftAisleValid && rightAisleValid) ||
        contiguousSeatBlockHasSingleAvailableSeat(data, selectedSeats, seat.position));
};
const hasAvailableSeatGapFromAisle = (data, selectedSeats, position, direction) => {
    return (seatOneSpaceAwayIsAvailable(data, selectedSeats, position, direction) &&
        seatTwoSpacesAwayIsAisle(data, position, direction));
};
const allSeatsBetweenSeatAndNextUnavailableSeatAreSelected = (data, selectedSeats, position, direction) => {
    const seatsUntilUnavailableSeat = getSeatsUntil(data, position, direction, s => isSeatUnavailable(data, s));
    return seatsUntilUnavailableSeat.every(s => isSeatSelected(selectedSeats, s));
};
const seatTwoSpacesAwayIsAisle = (data, position, direction) => {
    const seatTwoSpacesAway = getSeatTwoSpacesAway(data, position, direction);
    return seatTwoSpacesAway === undefined;
};
